import store from '../store/store';
import * as AlertActions from '../store/actions/alertActions';
import {md5hash} from "./picos/hash";
import {captureException} from "./picos/error";

export type AlertLevel = 'info' | 'warning' | 'error' |'success';

/**
 * Redux-based alerts.
 *
 * 1. All messages should be routed to a single point (this) for display.
 *
 * 2. Display should be at the App level, not every component.
 *    This class is imported by every class, but only App will have a display mechanism.
 *
 * 3. Stop duplicate messages from showing for the same item/error.
 *    We will use the item's id, addedOn and fatalError properties for display and culling.
 *
 * 4. Single helper class (this!) to import no matter where you are.
 */
class Alert {
    public static INFO: AlertLevel = 'info';
    public static WARNING: AlertLevel = 'warning';
    public static ERROR: AlertLevel = 'error';
    public static SUCCESS: AlertLevel = 'success';

    type: AlertLevel;
    id?: string | null;
    addedOn?: number | null;
    message: string;
    seen: boolean;
    hash: string;
    duration: number;
    /** Whether a failed delete burst durably removed any rows before stopping. */
    deleteQueuePartialSuccess?: boolean;

    /**
     * The Alert class encapsulates everything needed to throw an alert.
     *
     * @param {AlertLevel} type - info, warning, error, success
     * @param {string|number|null} id - ID of the entity throwing the error (null when
     *   the alert isn't tied to a specific entity, e.g. the addSimple* helpers)
     * @param {number|null} addedOn - addedOn of the entity (null/undefined when the
     *   alert isn't tied to an entity, or the entity isn't persisted yet)
     * @param {string} message - the message to display
     * @param {number} [duration] - optional duration override
     */
    // OWA-554: id and addedOn are genuinely nullable — the addSimple*/addQuickFade
    // helpers pass null, and callers pass an unpersisted entity's null addedOn. Both
    // are interpolated into strings (`${id}`/`${this.addedOn}`) which handle null
    // fine. The types were lying; make them truthful.
    constructor(type: AlertLevel, id: string | number | null, addedOn: number | null | undefined, message: string, duration: number = 5000) {
        this.type = type;
        this.id = `${id}`;
        this.addedOn = addedOn;
        this.message = message;
        this.seen = false;
        this.hash = md5hash(`${this.type}:${this.id}:${this.addedOn}:${this.message}`);
        this.duration = duration || 0;
    }

    /**
     * Add an alert to the queue.
     *
     * @param {Alert} alert
     */
    public static add(alert: Alert) {
        if (alert.type === Alert.ERROR) {
            // Capture the exception to external logger
            captureException(new Error(alert?.message));
        }

        // If no message, don't add the alert
        if (!alert?.message) return;

        // Log to the browser console
        if (alert.type === Alert.ERROR) console.error(alert?.message);
        else if (alert.type === Alert.WARNING) console.warn(alert?.message);

        // Dispatch the alert
        store.dispatch(AlertActions.addAlert(alert));
    };

    /**
     *
     * @param {string} message - error message to display
     * @param {number} [duration] - in milliseconds
     */
    public static addSimpleError(message: string, duration: number = 5_000) {
        try {
            // Capture the exception to external logging
            captureException(new Error(message));

            // Log to the console for the browser
            console.error(message);

            // Create a new alert with the given message and duration (defaults to 5 seconds)
            const alert = new Alert(Alert.ERROR, null, 0, message, duration);

            // Dispatch the alert
            store.dispatch(AlertActions.addAlert(alert));
        }
        catch (err) {
            console.error('Error showing error alert', err);
        }
    }

    /**
     *
     * @param {string} message - error message to display
     * @param {number} [duration] - in milliseconds
     */
    public static addSimpleInfo(message: string, duration: number = 5_000) {
        try {
            // Create a new alert with the given message and duration (defaults to 5 seconds)
            const alert = new Alert(Alert.INFO, null, 0, message, duration);

            // Dispatch the alert
            store.dispatch(AlertActions.addAlert(alert));
        }
        catch (err) {
            console.error('Error showing info alert', err);
        }
    }

    /**
     * Adds a simple success alert with a default duration of 2.5 seconds.
     *
     * @param {string} message - The success message to display.
     */
    public static addSimpleSuccess(message: string) {
        try {
            // Default to 2.5 seconds
            const alert = new Alert(Alert.SUCCESS, null, 0, message, 2_500);

            // Dispatch the alert
            store.dispatch(AlertActions.addAlert(alert));
        }
        catch (err) {
            console.error('Error showing success alert', err);
        }
    }

    public static addQuickFade(message: string) {
        try {
            // Default to 1.5 seconds
            const alert = new Alert(Alert.INFO, null, 0, message, 1_500);

            // Dispatch the alert
            store.dispatch(AlertActions.addAlert(alert));
        }
        catch (err) {
            console.error('Error showing quick fade', err);
        }
    }

    /**
     * Add more than one alert at a time, while only issuing one change to redux.
     *
     * @param {Array.<Alert>} alerts
     */
    public static addMany(alerts: Array<Alert>) {
        if (alerts.length > 0) store.dispatch(AlertActions.addAlerts(alerts));
    };

    /**
     * Remove an alert from the redux.
     *
     * @param {Alert} alert
     */
    public static remove(alert: Alert) {
        store.dispatch(AlertActions.removeAlert(alert));
    };

    /**
     * Remove many alerts in one batch, only issuing one change.
     *
     * @param {Array.<Alert>} alerts
     */
    public static removeMany(alerts: Array<Alert>) {
        store.dispatch(AlertActions.removeAlerts(alerts));
    };
}

export default Alert;
